/**
 * The pieces every bottom analysis dock's header is built from.
 *
 * `StationVolumeDock` and `ClearanceDock` are deliberately parallel components
 * rather than one component with a `kind` prop (ruling R2) — but their header
 * *chrome* is the same instrument reading in both: an eyebrow over a name, a
 * row of bordered chips saying how the figures were measured, and a row of
 * small-caps KPIs. Those three were copied from one dock into the other and
 * then drifted (the clearance chip was permanently amber, its KPI used a
 * different numeral class and printed its unit inside the value), which is the
 * drift this module exists to stop.
 *
 * Both parts keep `StationVolumeDock`'s markup byte-for-byte, including the
 * `sv-*` class names: those are defined by `SV_TOKENS`, which every dock
 * already injects and scopes to `.sv-root`, so there is no second palette to
 * keep in step. Nothing here is dock-specific — a caller supplies the strings
 * and the test ids.
 *
 * `DockPanel` below is the same idea one level out: the collapsible card the
 * *overlay* analysis panels (`DeviationVolumePanel`, `SurfaceDifferencePanel`)
 * are drawn on. They stack in one column and had drifted on eight separate
 * chrome details; see its own doc comment for how each was resolved.
 */
import type { ReactNode } from 'react';

import { ChevronDown, ChevronUp } from '@mynaui/icons-react';

import { cn } from '@/lib/utils';

import { DOCK_TEXT } from './dockStyles';

/**
 * The spacing scale the analysis column is built from — THREE rungs, one per
 * density, because a card, a divided sub-section and a numeric table row are
 * not the same kind of space and forcing them to one number reads worse, not
 * better.
 *
 *  - **`card`** — the outer padding of anything that floats over the viewport
 *    as its own surface: `DockPanel` and the `DeviationColorbar` stacked above
 *    it in the same column. `px-3 py-2` is the colorbar's own long-standing
 *    value; the panels were matched TO it in the previous round rather than the
 *    other way round, because it is the widest thing in the column and sets the
 *    optical left edge everything else lines up on.
 *  - **`section`** — a block inside a card, separated from what precedes it by
 *    a rule: the deviation panel's worst-case footer and per-surface
 *    disclosure, the surface panel's compliance row. It carries the rule and
 *    its colour as well as the spacing, so the three copies cannot drift on the
 *    border either (they already had: one was `dark:border-stone-700`, the
 *    others `…/70`).
 *  - **`cell`** — a dense numeric table row. Deliberately much tighter than
 *    `section`: these tables are scanned down a column of figures, and
 *    card-scale padding on a `text-[11px]` row spreads six values over a screen
 *    of whitespace. Asymmetric on purpose — vertical rhythm comes from the row
 *    height, horizontal separation from `pr`, and a trailing `pl` would push
 *    the first column off the card's own left edge.
 *
 * Not on the scale, and deliberately so:
 *  - `StationVolumesChip`'s `px-2 py-1` and `SV_TOKENS`' `.sv-chip` — an inline
 *    pill sized to its own text, already single-sourced in one component each.
 */
export const DOCK_PAD = {
  card: 'px-3 py-2',
  section: 'mt-2 border-t border-slate-200/70 pt-2 dark:border-stone-700/70',
  cell: 'py-0.5 pr-2',
} as const;

export interface DockKpiProps {
  label: string;
  value: string;
  unit?: string;
  /**
   * The station dock's m³ KPIs share one id (the unit is stated once for the
   * row); coverage states its own, so "the unit appears once" stays a testable
   * claim.
   */
  unitTestId?: string;
  color?: string;
  testId?: string;
  /**
   * @deprecated Ignored. Every KPI is one size now (spec §B1: "every KPI is the
   * same size; drop `hero`") — a dock header with two figure sizes read as two
   * rows of different importance when they are one row of peers, and the
   * verdict chip, not a bigger number, is what a reader looks at first. Still
   * accepted so callers compile until they drop it.
   */
  hero?: boolean;
  /**
   * Changes when the figure's SCOPE changes (corridor <-> brush), which
   * remounts the value span and replays the 90 ms cross-fade.
   */
  scopeKey?: string;
  /** Why the figure is what it is — an approximation note, a caveat. */
  title?: string;
  /**
   * A STATION, not a quantity: rendered in the same monospace tabular face the
   * range and the selection use, so the digits line up with every other
   * station in the dock instead of drifting against the proportional text.
   */
  mono?: boolean;
}

/**
 * One header figure: an 11 px label over a 16 px tabular value, about 36 px
 * tall — the one KPI size in every dock header and overlay card (spec §B0.3).
 * Neither line wraps: a KPI that breaks onto two lines throws the strip's
 * baseline, so a strip short of room hides whole KPIs by priority
 * (`DockKpiStrip`) instead.
 */
export const DockKpi = ({
  label,
  value,
  unit,
  unitTestId = 'sv-total-unit',
  color,
  testId,
  scopeKey,
  title,
  mono = false,
}: DockKpiProps) => (
  <div className='flex min-w-0 flex-col' title={title}>
    <span className={`${DOCK_TEXT.metaMuted} tracking-wide whitespace-nowrap uppercase`}>{label}</span>
    <span
      className={`${mono ? 'sv-sta' : 'sv-num'} ${DOCK_TEXT.kpi} whitespace-nowrap`}
      style={color ? { color } : undefined}
    >
      <span key={scopeKey} className='sv-xfade inline-block' data-testid={testId}>
        {value}
      </span>
      {/* No unit on an em dash: "— m" reads as a measured figure with its
          digits missing, when the dash means there is no figure at all. */}
      {unit && value !== '—' ? (
        <span className='sv-unit ml-1 font-normal' data-testid={unitTestId}>
          {unit}
        </span>
      ) : null}
    </span>
  </div>
);

export interface DockChipProps {
  children: ReactNode;
  title?: string;
  /** `'warn'` is the amber "read this before you quote it" tone. */
  tone?: 'neutral' | 'warn';
  testId?: string;
}

/** A header chip: one bordered phrase about how the figures were measured. */
export const DockChip = ({ children, title, tone = 'neutral', testId }: DockChipProps) => (
  <span
    data-testid={testId}
    title={title}
    // The tone lives in SV_TOKENS, not in a Tailwind class: `.sv-chip` sets
    // `border` from an unlayered rule, which beats Tailwind v4's layered
    // utilities — an amber border class here would silently lose.
    // 10 px, not the header's 12: a chip is reference material qualifying the
    // figures, and at the same size as the KPI labels it competed with them
    // for the same glance.
    className={`text-[10px] ${tone === 'warn' ? 'sv-chip sv-chip--warn' : 'sv-chip text-slate-600 dark:text-stone-300'}`}
  >
    {children}
  </span>
);

export interface DockPanelProps {
  /** The panel's name, in the header beside the chevron. */
  title: ReactNode;
  expanded: boolean;
  onToggle: () => void;
  /**
   * One string used as BOTH `aria-label` and `title` on the toggle. They are
   * not alternatives: the first is what a screen reader announces for a button
   * whose only permanent child is an icon, the second is the hover tooltip a
   * sighted user gets. The two panels had picked one each.
   */
  toggleLabel: string;
  /**
   * The collapsed-state one-liner, rendered INSIDE the toggle after the title
   * (the deviation panel's net + tolerance). Whether it shows only while
   * collapsed is the caller's business, not this shell's.
   */
  summary?: ReactNode;
  /** The right end of the header row. Both overlay panels pass the same
   *  `DockPanelActions` (layer name + PDF report button) — see that file. */
  actions?: ReactNode;
  /** Extra container classes — e.g. the surface panel's `sv-palette`. */
  className?: string;
  /**
   * The body. This shell deliberately does NOT gate it on `expanded` — the
   * caller does, and it is the caller that owns the editorial call about what
   * survives a collapse. Today both panels gate their WHOLE body: a collapsed
   * card is its header line and nothing else, on either of them. The surface
   * panel used to keep its compliance row out of the gate so a FAIL verdict
   * could never be hidden by a collapse; that intent now lives in its
   * COLLAPSED SUMMARY, which is visible in strictly more states than the row
   * was.
   */
  children?: ReactNode;
}

/**
 * The collapsible card the overlay analysis panels are drawn on.
 *
 * `DeviationVolumePanel` and `SurfaceDifferencePanel` sit one above the other
 * in `AnalysisColumnWindow` and had drifted apart on every detail of this
 * chrome. The resolutions, so nobody re-litigates them one file at a time:
 *
 *  - **`w-full`, never a fixed width.** These are flex children of the `w-96`
 *    scroll column, under a `DeviationColorbar` that has no width class and so
 *    fills it. A fixed `w-72` on the surface panel made it visibly narrower
 *    than the colorbar directly above it and the column read as ragged. If you
 *    are about to "tidy" a width back in here, that is the bug you are
 *    reintroducing.
 *  - **`bg-white/90` + `backdrop-blur-md`**, matching the colorbar above: these
 *    float over a live 3D viewport and the blur is what keeps them legible
 *    over a busy scene. An opaque-ish surface with no blur is not.
 *  - **`DOCK_PAD.card`** (`px-3 py-2`), again the colorbar's own padding, so
 *    the column is one system rather than three. (It was `px-3 py-1.5` and
 *    `p-3`.) It is spelled as the shared constant now, not as a literal, so
 *    the whole column moves together if the scale ever does.
 *  - **`shadow-sm`, `dark:border-stone-700/70`.** With the blur doing the
 *    separating, the shadow is only edge definition, and the lighter of the
 *    two stacks better down a column of cards; a solid dark border would be
 *    the one hard edge on a translucent surface.
 *  - **`font-medium` + an explicit ink for the title.** `font-semibold` is what
 *    `DockKpi` gives the *figures*, and a title at the same weight competes
 *    with them; the explicit colour matters because an inherited one is
 *    whatever the ancestor happened to set in dark mode.
 *  - **The whole header is the collapse target**, chevron and title in one
 *    button. A chevron-only hit area is a small target for a frequent action,
 *    and the title is the obvious thing to click.
 *  - **`size-3.5` chevron**, matching the other icon buttons in these headers
 *    and the `text-xs` copy it sits next to.
 */
export const DockPanel = ({
  title,
  expanded,
  onToggle,
  toggleLabel,
  summary,
  actions,
  className,
  children,
}: DockPanelProps) => (
  <div
    className={cn(
      'pointer-events-auto w-full rounded-md border border-slate-200/70 bg-white/90 text-xs shadow-sm backdrop-blur-md dark:border-stone-700/70 dark:bg-stone-900/90',
      DOCK_PAD.card,
      className,
    )}
  >
    <div className='flex min-w-0 items-center justify-between gap-2'>
      <button
        type='button'
        onClick={onToggle}
        aria-label={toggleLabel}
        title={toggleLabel}
        // `min-w-0 flex-1`, and the summary inside `truncate`s (audit C3). This
        // toggle used to be `shrink-0` on the theory that the summary is short;
        // the clearance card's summary is a verdict badge plus "7,600,302
        // violations · max 0.412 m", which is not, and a `shrink-0` button
        // wider than the card overflowed it and clipped the badge off the
        // right edge. The chevron and the title never shrink; the summary
        // gives first, ending in an ellipsis INSIDE the card.
        className='flex min-w-0 flex-1 items-center gap-1.5 text-left'
      >
        {expanded ? <ChevronUp className='size-3.5 shrink-0' /> : <ChevronDown className='size-3.5 shrink-0' />}
        <span className='shrink-0 font-medium text-slate-900 dark:text-white'>{title}</span>
        {summary != null && summary !== false && (
          <span data-testid='dock-panel-summary' className='min-w-0 truncate'>
            {summary}
          </span>
        )}
      </button>
      {/* `shrink-0`, capped at 45 % of the row: the actions are icon buttons
          (never squeezed — see `DockPanelActions`' 14 px note) and, on the
          volume cards, a layer name that truncates itself
          (`DockPanelLayerName`). The cap is what lets that name truncate at
          all inside a box that does not shrink, and keeps a long name from
          taking the summary's room. */}
      {actions != null && (
        <div className='flex max-w-[45%] min-w-0 shrink-0 items-center justify-end gap-1'>{actions}</div>
      )}
    </div>
    {children}
  </div>
);

export interface DockStepButtonProps {
  /** -1 steps back (◀), 1 forward (▶). */
  dir: -1 | 1;
  /** What the button does — its accessible name. */
  label: string;
  /** The hover text: the label, or the reason it is inert. Defaults to `label`. */
  title?: string;
  /**
   * A DATA condition (nothing to step to, no walk), rendered as
   * `aria-disabled` + muted, never the HTML `disabled` attribute: that would
   * take the button out of hover and the tab order, and the reason in its
   * title could never reach the reader.
   */
  disabled?: boolean;
  onClick: () => void;
  testId?: string;
  /** Extra classes, e.g. the joined-group rounding `DockStepper` (DockToolbar.tsx) applies. */
  className?: string;
}

/**
 * The ◀ / ▶ station step button every analysis dock uses — the station
 * section's stepper, the clearance cross-section's, and the clearance header's
 * previous / next violation. ONE component, because three hand-copied buttons
 * had drifted (lucide chevrons at `h-7 text-sm` in one header, the glyphs at
 * `h-6` in the sections) and the owner read them as three different controls.
 * The click is swallowed while `disabled`, so callers need not guard it.
 */
export const DockStepButton = ({
  dir,
  label,
  title,
  disabled = false,
  onClick,
  testId,
  className,
}: DockStepButtonProps) => (
  <button
    type='button'
    data-testid={testId}
    aria-label={label}
    title={title ?? label}
    aria-disabled={disabled}
    onClick={() => {
      if (!disabled) onClick();
    }}
    className={cn(
      'flex h-7 min-w-7 shrink-0 items-center justify-center rounded border px-1 text-xs focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none',
      disabled
        ? 'border-slate-200 opacity-50 dark:border-stone-700'
        : 'border-slate-300 hover:bg-slate-200/70 dark:border-stone-600 dark:hover:bg-stone-700/70',
      className,
    )}
  >
    {dir === -1 ? '◀' : '▶'}
  </button>
);
